import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { DeliveryNote, DeliveryNoteItem } from '../../types';
import Button from './Button';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import { useBranches } from '../../hooks/useBranches';
import { useRequisitions } from '../../hooks/useRequisitions';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import {
  DeliveryNoteHeader,
  WorkSiteVesselSection,
  TechnicalPartTable,
  DeliverySignaturesSection,
} from './DeliveryNoteSections';

interface DeliveryNoteFormProps {
  deliveryNote?: DeliveryNote;
  requisitionId?: string;
  onSubmit: (data: Partial<DeliveryNote>) => void;
  onCancel: () => void;
}

const DeliveryNoteForm: React.FC<DeliveryNoteFormProps> = ({ 
  deliveryNote, 
  requisitionId,
  onSubmit, 
  onCancel 
}) => {
  const { data: branchesData } = useBranches();
  const { data: requisitionsData } = useRequisitions();
  const codePrefixQueryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(codePrefixQueryOptions);

  const [selectedCodePrefix, setSelectedCodePrefix] = useState<string>('');
  const [dnSeqNumber, setDnSeqNumber] = useState<string>('001');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue
  } = useForm<DeliveryNote>({
    defaultValues: deliveryNote ? {
      ...deliveryNote,
      date: formatDateForInput(deliveryNote.date),
      deliveryDate: formatDateForInput(deliveryNote.deliveryDate),
      receivedDate: formatDateForInput(deliveryNote.receivedDate),
      // API stores the signature blocks flat; rebuild the nested form shape
      deliveredBy: deliveryNote.deliveredBy || {
        name: (deliveryNote as any).deliveredByName || '',
        signature: (deliveryNote as any).deliveredBySignature || '',
      },
      deliveredTo: deliveryNote.deliveredTo || {
        name: (deliveryNote as any).deliveredToName || '',
        signature: (deliveryNote as any).deliveredToSignature || '',
        zoom: (deliveryNote as any).deliveredToZoom || '',
      },
    } : {
      requisitionId: requisitionId || '',
      deliveryNoteNumber: '',
      workSite: '',
      vessel: '',
      date: new Date().toISOString().split('T')[0],
      noteNo: '',
      projectLocation: '',
      technicalPart: [
        { id: '1', no: 1, type: '', quantity: 0, unitSize: '' }
      ],
      deliveredBy: { signature: '', name: '' },
      deliveredTo: { signature: '', name: '', zoom: '' },
      stampDeliveredBy: '',
      stampDeliveredTo: '',
      pageNumber: 'Page: 1 of 1',
      status: 'pending',
      deliveryDate: new Date().toISOString().split('T')[0],
      notes: ''
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'technicalPart'
  });

  useEffect(() => {
    if (deliveryNote?.deliveryNoteNumber && (codePrefixes as any[]).length > 0) {
      const matchingPrefix = (codePrefixes as any[]).find((p: any) =>
        deliveryNote.deliveryNoteNumber.startsWith(p.prefix)
      );
      if (matchingPrefix) {
        setSelectedCodePrefix(matchingPrefix.id);
        setDnSeqNumber(deliveryNote.deliveryNoteNumber.replace(matchingPrefix.prefix, ''));
      }
    }
  }, [deliveryNote, codePrefixes]);

  const selectedRequisitionId = watch('requisitionId');

  const vesselOptions = useMemo(() => {
    if (!branchesData || branchesData.length === 0) return [];
    return branchesData
      .filter((branch: any) => branch.type === 'vessel')
      .map((vessel: any) => ({
        value: vessel.name,
        label: vessel.name
      }));
  }, [branchesData]);

  const requisitionOptions = useMemo(() => {
    if (!requisitionsData || requisitionsData.length === 0) return [];
    return requisitionsData
      .filter((req: any) => req.status === 'purchased')
      .map((req: any) => ({
        value: req.documentId || req.id,
        label: `${req.formNumber} - ${req.vessel} - ${req.department}`
      }));
  }, [requisitionsData]);

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'received', label: 'Received' },
    { value: 'completed', label: 'Completed' }
  ];

  useEffect(() => {
    if (!deliveryNote) {
      const timestamp = Date.now().toString().slice(-6);
      setValue('deliveryNoteNumber', `DN-${timestamp}`);
    }
  }, [deliveryNote, setValue]);

  useEffect(() => {
    if (selectedRequisitionId && !deliveryNote && requisitionsData) {
      const requisition = requisitionsData.find((req: any) => (req.documentId || req.id) === selectedRequisitionId);
      if (requisition) {
        setValue('vessel', requisition.vessel);
        setValue('projectLocation', requisition.location);
        setValue('workSite', requisition.location);
        
        const technicalParts = (Array.isArray(requisition.items) ? requisition.items : []).map((item: any, index: number) => ({
          id: (index + 1).toString(),
          no: index + 1,
          type: item.item,
          quantity: item.appQty,
          unitSize: item.rob
        }));
        setValue('technicalPart', technicalParts);
      }
    }
  }, [selectedRequisitionId, deliveryNote, setValue, requisitionsData]);

  const addItem = () => {
    const newItem: DeliveryNoteItem = {
      id: Date.now().toString(),
      no: fields.length + 1,
      type: '',
      quantity: 0,
      unitSize: ''
    };
    append(newItem);
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      setTimeout(() => {
        fields.forEach((_, i) => {
          if (i > index) {
            setValue(`technicalPart.${i}.no`, i + 1);
          }
        });
      }, 0);
    }
  };

  const onFormSubmit = (data: DeliveryNote) => {
    const processedData = {
      ...data,
      date: formatDateForAPI(data.date),
      deliveryDate: formatDateForAPI(data.deliveryDate),
      receivedDate: formatDateForAPI(data.receivedDate),
      technicalPart: data.technicalPart.map((item, index) => ({
        ...item,
        id: item.id || Date.now().toString() + index,
        no: index + 1
      }))
    };
    onSubmit(processedData);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {deliveryNote ? 'Edit Delivery Note' : 'Create Delivery Note'}
          </h1>
          <p className="text-gray-600">
            {deliveryNote ? 'Update delivery note details' : 'Create a new delivery note for purchased requisition'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        <DeliveryNoteHeader
          register={register}
          errors={errors}
          codePrefixes={codePrefixes as any[]}
          selectedCodePrefix={selectedCodePrefix}
          setSelectedCodePrefix={setSelectedCodePrefix}
          dnSeqNumber={dnSeqNumber}
          setDnSeqNumber={setDnSeqNumber}
          setValue={setValue}
          requisitionOptions={requisitionOptions}
          isEditing={!!deliveryNote}
          hasRequisitionId={!!requisitionId}
        />

        <WorkSiteVesselSection
          register={register}
          errors={errors}
          vesselOptions={vesselOptions}
        />

        <TechnicalPartTable
          register={register}
          fields={fields}
          addItem={addItem}
          removeItem={removeItem}
        />

        <DeliverySignaturesSection
          register={register}
          errors={errors}
          statusOptions={statusOptions}
        />

        <div className="flex justify-end space-x-4">
          <Button type="button" onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : deliveryNote ? 'Update Delivery Note' : 'Create Delivery Note'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DeliveryNoteForm;
